/**
 * Primitivos compartidos por todas las colecciones.
 *
 * - `Timestamp` es una interfaz ESTRUCTURAL: el SDK cliente (`firebase/firestore`) y el
 *   Admin SDK (`firebase-admin/firestore`) tienen clases Timestamp distintas; declarando
 *   solo la forma, `@nuvvu/shared` compila en los dos lados sin depender de ninguno.
 * - `Money` es SIEMPRE un entero en la unidad menor (céntimos, fillér, øre…). Nunca se
 *   guarda un float: los floats son la causa de los descuadres de céntimos en el wallet.
 * - Los ids son tipos "branded": un `UserId` no se puede pasar donde va un `OrderId`.
 */
import { z } from 'zod';

// ---------- Timestamp ----------

export interface Timestamp {
  readonly seconds: number;
  readonly nanoseconds: number;
  toMillis(): number;
  toDate(): Date;
}

/** Acepta cualquier objeto con la forma de Timestamp (cliente o admin). */
export const TimestampSchema = z.custom<Timestamp>(
  (v): v is Timestamp =>
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Timestamp).seconds === 'number' &&
    typeof (v as Timestamp).nanoseconds === 'number' &&
    typeof (v as Timestamp).toMillis === 'function',
  { message: 'Se esperaba un Timestamp de Firestore' },
);

/**
 * Fechas ISO-8601 en string: así las guarda la 1.0 (`created_date`, `updated_date`,
 * `sold_at`, `reserved_until`…). Se mantiene en `products` por paridad byte a byte.
 */
export const IsoDateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?(Z|[+-]\d{2}:\d{2})$/, 'Fecha ISO-8601');
export type IsoDateString = z.infer<typeof IsoDateStringSchema>;

// ---------- Money ----------

/** ISO-4217 de las monedas que Nuvvu cobra o muestra (sección 10b, A7). */
export const CURRENCIES = [
  'EUR',
  'HUF',
  'CZK',
  'PLN',
  'RON',
  'SEK',
  'DKK',
  'NOK',
  'CHF',
  'BGN',
  'ISK',
] as const;
export const CurrencySchema = z.enum(CURRENCIES);
export type Currency = z.infer<typeof CurrencySchema>;

/** Decimales de cada moneda (ISO-4217). ISK y HUF se cobran sin decimales en Stripe. */
export const CURRENCY_MINOR_DIGITS: Readonly<Record<Currency, 0 | 2>> = {
  EUR: 2,
  HUF: 0,
  CZK: 2,
  PLN: 2,
  RON: 2,
  SEK: 2,
  DKK: 2,
  NOK: 2,
  CHF: 2,
  BGN: 2,
  ISK: 0,
};

export const MoneySchema = z.object({
  /** Entero en la unidad menor de `currency` (céntimos de euro, forintos enteros…). */
  amount: z.number().int(),
  currency: CurrencySchema,
});
export type Money = z.infer<typeof MoneySchema>;

export const NonNegativeMoneySchema = MoneySchema.extend({ amount: z.number().int().min(0) });

export function money(amount: number, currency: Currency): Money {
  if (!Number.isInteger(amount)) throw new TypeError(`Money.amount debe ser entero: ${amount}`);
  return { amount, currency };
}

export function sameCurrency(a: Money, b: Money): boolean {
  return a.currency === b.currency;
}

export function addMoney(a: Money, b: Money): Money {
  if (!sameCurrency(a, b)) throw new TypeError(`Monedas distintas: ${a.currency} vs ${b.currency}`);
  return { amount: a.amount + b.amount, currency: a.currency };
}

export function subMoney(a: Money, b: Money): Money {
  if (!sameCurrency(a, b)) throw new TypeError(`Monedas distintas: ${a.currency} vs ${b.currency}`);
  return { amount: a.amount - b.amount, currency: a.currency };
}

export const zeroMoney = (currency: Currency): Money => ({ amount: 0, currency });

// ---------- Ids branded ----------

/** Brand por propiedad fantasma (no existe en runtime); nameable en los .d.ts generados. */
export type Brand<T, B extends string> = T & { readonly __brand: B };

export type UserId = Brand<string, 'UserId'>;
export type ProductId = Brand<string, 'ProductId'>;
export type OrderId = Brand<string, 'OrderId'>;
export type PaymentId = Brand<string, 'PaymentId'>;
export type ShipmentId = Brand<string, 'ShipmentId'>;
export type CarrierId = Brand<string, 'CarrierId'>;
export type DisputeId = Brand<string, 'DisputeId'>;
export type OfferId = Brand<string, 'OfferId'>;
export type BundleId = Brand<string, 'BundleId'>;
export type ConversationId = Brand<string, 'ConversationId'>;
export type WalletTxId = Brand<string, 'WalletTxId'>;
export type FcmToken = Brand<string, 'FcmToken'>;

const id = <T extends string>(name: string) =>
  z
    .string()
    .min(1)
    .max(1500)
    .transform((v) => v as Brand<string, T>)
    .describe(name);

export const UserIdSchema = id<'UserId'>('UserId');
export const ProductIdSchema = id<'ProductId'>('ProductId');
export const OrderIdSchema = id<'OrderId'>('OrderId');
export const PaymentIdSchema = id<'PaymentId'>('PaymentId');
export const ShipmentIdSchema = id<'ShipmentId'>('ShipmentId');
export const CarrierIdSchema = id<'CarrierId'>('CarrierId');
export const DisputeIdSchema = id<'DisputeId'>('DisputeId');
export const OfferIdSchema = id<'OfferId'>('OfferId');
export const BundleIdSchema = id<'BundleId'>('BundleId');
export const ConversationIdSchema = id<'ConversationId'>('ConversationId');
export const WalletTxIdSchema = id<'WalletTxId'>('WalletTxId');
export const FcmTokenSchema = id<'FcmToken'>('FcmToken');

/** Para construir ids en tests y scripts sin pelear con el brand. */
export const asUserId = (s: string): UserId => s as UserId;
export const asProductId = (s: string): ProductId => s as ProductId;
export const asOrderId = (s: string): OrderId => s as OrderId;
export const asPaymentId = (s: string): PaymentId => s as PaymentId;
export const asShipmentId = (s: string): ShipmentId => s as ShipmentId;
export const asWalletTxId = (s: string): WalletTxId => s as WalletTxId;
export const asCarrierId = (s: string): CarrierId => s as CarrierId;
export const asDisputeId = (s: string): DisputeId => s as DisputeId;
export const asOfferId = (s: string): OfferId => s as OfferId;
export const asBundleId = (s: string): BundleId => s as BundleId;

// ---------- Otros ----------

/** Código de país ISO-3166-1 alpha-2 en mayúsculas. */
export const CountryCodeSchema = z.string().regex(/^[A-Z]{2}$/, 'ISO-3166-1 alpha-2');
export type CountryCode = z.infer<typeof CountryCodeSchema>;

/** Idioma soportado (los 33 de la sección 13). La lista canónica vive en @nuvvu/i18n. */
export const LanguageCodeSchema = z.string().regex(/^[a-z]{2}$/, 'ISO-639-1');
export type LanguageCode = z.infer<typeof LanguageCodeSchema>;

/** Texto en varios idiomas: { es: '...', en: '...' }. */
export const LocalizedTextSchema = z.record(LanguageCodeSchema, z.string());
export type LocalizedText = z.infer<typeof LocalizedTextSchema>;

/** Quién hizo algo: para `history[]` y `auditLog`. */
export const ActorSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('buyer'), uid: UserIdSchema }),
  z.object({ type: z.literal('seller'), uid: UserIdSchema }),
  z.object({ type: z.literal('admin'), uid: UserIdSchema }),
  z.object({ type: z.literal('system'), job: z.string().min(1) }),
]);
export type Actor = z.infer<typeof ActorSchema>;
